import React from 'react';
import './SocialUserCard.css';

// Shared social user card based on Discover UserGrid card.
// This component is intentionally presentational; all behaviors are driven via props
// so different sections (Discover, Birthdays, Groups, Events, Profile, Academy) can
// reuse the same visual without forcing one behavior model.

const getInitials = (name = '') => {
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0][0]?.toUpperCase() || '';
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const SocialUserCard = ({
  user,
  isSelf = false,
  isConnected = false,
  isPending = false,
  canMessage = false,
  isOnline = false,
  isNew = false,
  isFavorited = false,
  hasMutual = false,
  isVerified = false,
  mutualLabel = null,
  extraBadge = null, // e.g. birthday or role label
  onFavoriteToggle,
  onClick,
  onOpenProfile,
  onConnect,
  onMessage,
  // Quick actions: array of { label, onClick }
  quickActions = [],
  // Enable internal hover details overlay
  showHoverDetails = false,
}) => {
  const displayName = user?.display_name || user?.name || user?.email || 'User';

  const handleRootClick = () => {
    if (onClick) onClick(user);
    else if (onOpenProfile && user?.id) onOpenProfile(user);
  };

  const handleFavorite = (e) => {
    e.stopPropagation();
    if (onFavoriteToggle && user?.id) onFavoriteToggle(user);
  };

  const native = Array.isArray(user?.native_languages) ? user.native_languages : [];
  const target = Array.isArray(user?.target_languages) ? user.target_languages : [];
  const interests = Array.isArray(user?.interests) ? user.interests : [];
  const shortBio = user?.bio ? String(user.bio).slice(0, 120) : null;
  const hasLongBio = user?.bio && String(user.bio).length > 120;

  return (
    <div
      className="social-user-card user-card"
      tabIndex={0}
      onClick={handleRootClick}
    >
      {onFavoriteToggle && (
        <button
          className={`favorite-btn${isFavorited ? ' favorited' : ''}`}
          title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
          onClick={handleFavorite}
          aria-label={isFavorited ? 'Unfavorite user' : 'Favorite user'}
        >
          {isFavorited ? '★' : '☆'}
        </button>
      )}

      {quickActions && quickActions.length > 0 && (
        <div className="quick-actions-dropdown">
          {/* Caller can render its own trigger or we provide a simple one */}
          <button
            className="quick-actions-btn"
            type="button"
            onClick={(e) => e.stopPropagation()}
            aria-label="Quick actions"
          >
            ⋮
          </button>
          {/* For now we render a simple always-open menu when quickActions present.
              Discover keeps its richer stateful menu. Other sections can ignore. */}
          <div className="quick-actions-menu">
            {quickActions.map((action, idx) => (
              <button
                key={idx}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  action.onClick?.(user);
                }}
              >
                {action.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="user-avatar-wrapper" title={displayName}>
        {user?.avatar_url ? (
          <img
            src={user.avatar_url}
            alt={displayName}
            className="user-avatar"
          />
        ) : (
          <div
            className="user-avatar user-avatar-fallback"
            style={{
              backgroundColor: `hsl(${(user?.id?.length || 1) * 60 % 360}, 70%, 60%)`,
            }}
          >
            {getInitials(displayName)}
          </div>
        )}
        {isOnline && <span className="user-online-dot" title="Online" />}
        {isNew && !isPending && !isConnected && (
          <span className="user-badge new-badge" title="New user">New</span>
        )}
        {isConnected && <span className="user-badge connected-badge" title="Connected">Connected</span>}
        {isPending && !isConnected && !isSelf && (
          <span className="user-badge pending-badge" title="Request pending">Pending</span>
        )}
        {isVerified && <span className="user-badge verified-badge" title="Verified">✔</span>}
        {hasMutual && (
          <span className="user-badge mutual-badge" title={mutualLabel || 'Mutual interests'}>
            Mutual
          </span>
        )}
        {extraBadge && (
          <span className="user-badge extra-badge" title={extraBadge.title || ''}>
            {extraBadge.label}
          </span>
        )}
      </div>

      <div className="user-info">
        <div className="user-name" title={displayName}>{displayName}</div>
        {(native.length > 0 || target.length > 0) && (
          <div className="user-languages" title={`Native: ${native.join(', ')} Target: ${target.join(', ')}`}>
            {native.length > 0 && <span>🗣️ {native.join(', ')}</span>}
            {target.length > 0 && <span>🎯 {target.join(', ')}</span>}
          </div>
        )}
        {user?.location && (
          <div className="user-location" title={user.location}>{user.location}</div>
        )}
        {user?.occupation && (
          <div className="user-occupation" title={user.occupation}>{user.occupation}</div>
        )}
        {interests.length > 0 && (
          <div className="user-interests" title={interests.join(', ')}>
            {interests.slice(0, 3).map((interest, idx) => (
              <span className="user-interest" key={idx}>{interest}</span>
            ))}
          </div>
        )}
      </div>

      <div className="user-card-actions" onClick={(e) => e.stopPropagation()}>
        {!isSelf && onConnect && !isConnected && (
          <button
            type="button"
            className="connect-btn"
            disabled={isPending}
            onClick={() => onConnect(user)}
            title={isPending ? 'Request sent' : 'Connect'}
          >
            {isPending ? 'Pending' : 'Connect'}
          </button>
        )}
        {!isSelf && canMessage && onMessage && (
          <button
            type="button"
            className="message-btn"
            onClick={() => onMessage(user)}
            title="Send a message"
          >
            Message
          </button>
        )}
        {isSelf && (
          <span className="self-badge" title="This is you">You</span>
        )}
      </div>
      {showHoverDetails && (
        <div
          className="user-hover-overlay"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="user-hover-overlay-header">
            <img
              src={user?.avatar_url || '/default-avatar.png'}
              alt={displayName}
              className="user-hover-avatar"
            />
            <div className="user-hover-meta">
              <div className="user-hover-name-row">
                <strong>{displayName}</strong>
                {isVerified && (
                  <span className="user-hover-verified" title="Verified">
                    ✔
                  </span>
                )}
                {isOnline && <span className="user-hover-online-dot" title="Online" />}
              </div>
              {user?.location && (
                <span className="user-hover-location">{user.location}</span>
              )}
              {user?.occupation && (
                <span className="user-hover-occupation">{user.occupation}</span>
              )}
            </div>
          </div>
          <div className="user-hover-languages">
            <div>🗣️ {native.length ? native.join(', ') : '—'}</div>
            <div>🎯 {target.length ? target.join(', ') : '—'}</div>
          </div>
          {shortBio && (
            <div className="user-hover-bio">
              {shortBio}
              {hasLongBio ? '…' : ''}
            </div>
          )}
          {interests.length > 0 && (
            <div className="user-hover-interests">
              {interests.slice(0, 6).map((interest, idx) => (
                <span key={idx} className="user-hover-interest-chip">
                  {interest}
                </span>
              ))}
            </div>
          )}
          <div className="user-hover-actions">
            <button
              type="button"
              className="visit-profile-btn"
              onClick={(e) => {
                e.stopPropagation();
                if (onClick) onClick(user);
                else if (onOpenProfile && user?.id) onOpenProfile(user);
              }}
            >
              Visit Profile
            </button>
            {!isSelf && canMessage && onMessage && (
              <button
                type="button"
                className="send-message-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onMessage(user);
                }}
              >
                Send Message
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SocialUserCard;
